import { CrowdctrlListComponent } from '../list/list.component';
import { LoadingCardComponent } from '../loading-card/loading-card.component';
import { Component, inject, ViewChild, OnInit, OnDestroy, Optional } from '@angular/core';
import { CommonModule, AsyncPipe } from '@angular/common';
import {
  IonButton,
  IonIcon,
  IonPopover,
  IonContent,
  IonItem,
  IonLabel,
  IonAvatar,
  IonSelectOption,
  IonSpinner,
} from '@ionic/angular/standalone';
import { SelectComponent } from '../select/select.component';
import { addIcons } from 'ionicons';
import { personCircle, logOut, settings, home } from 'ionicons/icons';
import { AuthService } from '../../services/auth.service';
import { PwaUpdateService } from '../../services/pwa-update.service';
import { ACCOUNT_LABEL } from '../../tokens/account-label.token';
import {
  FORM_LOCK_SERVICE_TOKEN,
  FormLockServiceInterface,
} from '../../tokens/form-lock-service.token';
import { TenantSelectionService } from '../../services/tenant-selection.service';
import { TenantService } from '../../services/tenant.service';
import { Router } from '@angular/router';
import { Subscription, firstValueFrom, of } from 'rxjs';
import { map, startWith } from 'rxjs/operators';

// Register icons
addIcons({ personCircle, logOut, settings, home });

@Component({
  selector: 'crowdctrl-user-profile-button',
  standalone: true,
  imports: [
    CommonModule,
    AsyncPipe,
    IonButton,
    IonIcon,
    IonPopover,
    IonContent,
    CrowdctrlListComponent,
    IonItem,
    IonLabel,
    IonAvatar,
    IonSelectOption,
    IonSpinner,
    SelectComponent,
    LoadingCardComponent,
  ],
  templateUrl: './user-profile-button.component.html',
  styleUrl: './user-profile-button.component.css',
})
export class UserProfileButtonComponent implements OnInit, OnDestroy {
  @ViewChild('popover') popover!: IonPopover;

  private authService = inject(AuthService);
  private pwaUpdateService = inject(PwaUpdateService);
  private tenantSelectionService = inject(TenantSelectionService);
  private tenantService = inject(TenantService);
  private router = inject(Router);
  private accountLabelToken = inject(ACCOUNT_LABEL);
  private formLockService = inject<FormLockServiceInterface | null>(FORM_LOCK_SERVICE_TOKEN, {
    optional: true,
  });

  user$ = this.authService.authState$;
  isPopoverOpen = false;
  isMultiTenant = false;
  availableTenants: Array<{ id: string; name: string }> = [];
  selectedTenantId: string | null = null;
  isTenantsLoading = false;
  accountLabel = this.accountLabelToken;
  accountLabelPlural = `${this.accountLabelToken}s`;
  accountLabelLower = this.accountLabelToken.toLowerCase();
  accountLabelPluralLower = this.accountLabelPlural.toLowerCase();
  private subscriptions = new Subscription();

  // Observable for lock state (returns false if service not available)
  isLocked$ = this.formLockService
    ? this.formLockService.isLocked$.pipe(startWith(this.formLockService.isLocked()))
    : of(false);

  updateDownloading$ = this.pwaUpdateService.updateDownloading$;

  get isTenantSelectorDisabled(): boolean {
    return !this.isMultiTenant || this.availableTenants.length <= 1;
  }

  openPopover(event: Event): void {
    this.popover.event = event;
    this.isPopoverOpen = true;
    this.popover.present();
  }

  async ngOnInit(): Promise<void> {
    this.isMultiTenant = await this.tenantSelectionService.isMultiTenant();

    // Always load tenant information, even for single-tenant users
    this.isTenantsLoading = true;
    const tenantIds = await this.tenantSelectionService.getAvailableTenants();
    if (!tenantIds || tenantIds.length === 0) {
      this.isTenantsLoading = false;
      return;
    }

    // Set the selected tenant ID before fetching tenant details
    // This is required because ApiService.get() requires a selected tenant ID
    const currentSelectedId = this.tenantSelectionService.getSelectedTenantId();
    if (!currentSelectedId && tenantIds.length > 0) {
      // Use the first tenant ID from the token
      await this.tenantSelectionService.setSelectedTenantId(tenantIds[0]);
    }

    this.subscriptions.add(
      this.tenantSelectionService.selectedTenantId$.subscribe((id) => {
        this.selectedTenantId = id;
      }),
    );

    const tenantPromises = tenantIds.map(async (id) => {
      try {
        // Temporarily set the selected tenant ID for this API call if needed
        const previousSelectedId = this.tenantSelectionService.getSelectedTenantId();
        if (previousSelectedId !== id) {
          await this.tenantSelectionService.setSelectedTenantId(id);
        }
        const response = await firstValueFrom(this.tenantService.getTenant(id));
        // Restore previous selection if it was different
        if (previousSelectedId && previousSelectedId !== id) {
          await this.tenantSelectionService.setSelectedTenantId(previousSelectedId);
        }
        if (response?.tenant) {
          return { id: response.tenant.id, name: response.tenant.name };
        }
        return null;
      } catch (error) {
        console.error('Error fetching tenant:', id, error);
        return null;
      }
    });

    const tenants = await Promise.all(tenantPromises);
    this.availableTenants = tenants.filter((tenant) => tenant !== null) as Array<{
      id: string;
      name: string;
    }>;
    this.isTenantsLoading = false;

    // Ensure a tenant is selected if we have tenants but none selected
    if (!this.selectedTenantId && this.availableTenants.length > 0) {
      this.tenantSelectionService.setSelectedTenantId(this.availableTenants[0].id);
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  async onTenantChange(event: Event): Promise<void> {
    // Don't allow tenant change if selector is disabled (single tenant)
    if (this.isTenantSelectorDisabled) {
      return;
    }

    const tenantId = (event as CustomEvent<{ value?: string | null }>).detail.value as
      | string
      | null;
    if (!tenantId) {
      return;
    }

    this.tenantSelectionService.setSelectedTenantId(tenantId);
    await this.popover.dismiss();
    this.isPopoverOpen = false;
    await this.router.navigateByUrl('/', { replaceUrl: true });
  }

  async goToHome(): Promise<void> {
    await this.popover.dismiss();
    this.isPopoverOpen = false;
    await this.router.navigate(['/']);
  }

  async goToSettings(): Promise<void> {
    await this.popover.dismiss();
    this.isPopoverOpen = false;
    await this.router.navigate(['/settings']);
  }

  async signOut(): Promise<void> {
    await this.popover.dismiss();
    this.isPopoverOpen = false;
    await this.authService.signOut();
  }
}
